import * as React from 'react';
import {translate, InjectedTranslateProps} from 'react-i18next';
import {subscribe, ViewableFold} from '../../utils/track-folds';
import {reportClick} from '../../utils/bi';
import LinkMore from '../LinkMore';
import Text from '../Text';

import WixCodeImage from '../Images/WixCodeImage';
import AdiImage from '../Images/AdiImage';
import EditorImage from '../Images/EditorImage';

import JaWixCodeImage from '../Images/JaWixCodeImage';
import JaAdiImage from '../Images/JaAdiImage';
import JaEditorImage from '../Images/JaEditorImage';

import experiments from '../../utils/experiments';
import * as s from './Products.scss';

const ANIMATED_PRODUCTS_VARIANT = 'animated-products';

type ProductImage = {
  adi: string;
  editor: string;
  wixCode: string;
};

export interface ProductConfig {
  staticsBaseUrl: string;
  productImages: ProductImage;
  key: string;
}

export interface ProductsProps extends InjectedTranslateProps, ProductConfig {
}

interface ProductsState {
  animated: boolean;
  columnHeight: number | 'auto';
}

const ProductImagesMap = {
  AdiImage,
  WixCodeImage,
  EditorImage,
  JaAdiImage,
  JaWixCodeImage,
  JaEditorImage
};

class Products extends React.PureComponent<ProductsProps, ProductsState> {
  private adiRef: HTMLElement;
  private codeRef: HTMLElement;
  private resizeTimeout: any;
  private columnHeightTimeout: any;
  private lastWindowWidth: number;

  constructor(props: ProductsProps) {
    super(props);
    this.state = {
      animated: false,
      columnHeight: experiments.get('specs.marketing.HomepageVariant') === ANIMATED_PRODUCTS_VARIANT ? 0 : 'auto'
    };
  }

  getMaxColumnHeight() {
    return Math.max(this.adiRef.offsetHeight, this.codeRef.offsetHeight);
  }

  onFoldChange = (folds: Array<ViewableFold>) => {
    const {animated} = this.state;
    folds.forEach(fold => {
      if (fold.foldName === 'products' && fold.windowPercentageOccupiedByFold > 30 && !animated && !this.isMobileView()) {
        const columnHeight = this.getMaxColumnHeight();
        this.setState({animated: true, columnHeight});
      }
    });
  }

  componentDidMount() {
    subscribe(this.onFoldChange);
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleResize);
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize);
    }
    clearTimeout(this.resizeTimeout);
    clearTimeout(this.columnHeightTimeout);
  }

  isMobileView() {
    return typeof window !== 'undefined' && window.innerWidth < 1024;
  }

  handleResize = () => {
    const currentWindowWidth = window.innerWidth;
    if (this.lastWindowWidth === currentWindowWidth) {
      return;
    }

    this.lastWindowWidth = currentWindowWidth;

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    if (this.columnHeightTimeout) {
      clearTimeout(this.columnHeightTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      if (this.isMobileView()) {
        this.setState({columnHeight: 'auto'});
        return;
      }

      this.setState({columnHeight: 'auto'});
      this.columnHeightTimeout = setTimeout(() => {
        const columnHeight = this.getMaxColumnHeight();
        this.setState({columnHeight});
      }, 100);
    }, 400);
  }

  setAdiRef = (ref: HTMLElement) => this.adiRef = ref;
  setCodeRef = (ref: HTMLElement) => this.codeRef = ref;

  render() {
    const {t, productImages: {adi, editor, wixCode}} = this.props;
    const {animated, columnHeight} = this.state;

    return (
      <div
        className={`page-fold ${s.container}${animated ? ` ${s.animated}` : ''}`}
        data-hook="products-fold"
        data-foldname="products"
        data-foldnum="3"
      >
        <div className={`${s.animationWrapper} ${s.productsWrapper}`}>
          <div className={`${s.product} ${s.wixEditor}`}>
            <div className={s.productImage}>
              {React.createElement(ProductImagesMap[editor], {animated})}
            </div>
            <div className={s.productInfo}>
              <div className={s.productInfoInner}>
                <div className={s.productInfoCopy}>
                  <Text parent="h3" i18nKey="products.product1.title"/>
                  <Text parent="h4" i18nKey="products.product1.subtitle"/>
                  <Text parent="p" className={s.productCopy} i18nKey="products.product1Text"/>
                </div>
              </div>
            </div>
            <LinkMore
              className={s.linkMore}
              data-hook="products-get-started products-get-started-editor"
              onClick={reportClick}
              data-foldname="products"
            >
              {t('button.getStarted')}
            </LinkMore>
          </div>
          <div className={`${s.product} ${s.wixAdi}`}>
            <div className={s.productImage}>
              {React.createElement(ProductImagesMap[adi], {animated})}
            </div>
            <div className={s.productInfo} style={{height: columnHeight}}>
              <div className={s.productInfoInner} ref={this.setAdiRef}
                   style={{height: animated ? columnHeight : 'auto'}}>
                <div className={s.productInfoCopy}>
                  <Text parent="h3" i18nKey="products.product2.title"/>
                  <Text parent="h4" i18nKey="products.product2.subtitle"/>
                  <Text parent="p" className={s.productCopy} i18nKey="products.product2Text"/>
                </div>
              </div>
            </div>
            <LinkMore
              className={s.linkMore}
              data-hook="products-get-started products-get-started-adi"
              onClick={reportClick}
              data-foldname="products"
            >
              {t('button.getStarted')}
            </LinkMore>
          </div>
          <div className={`${s.product} ${s.wixCode}`}>
            <div className={s.productImage}>
              {React.createElement(ProductImagesMap[wixCode], {animated})}
            </div>
            <div className={s.productInfo} style={{height: columnHeight}}>
              <div className={s.productInfoInner} ref={this.setCodeRef}
                   style={{height: animated ? columnHeight : 'auto'}}>
                <div className={s.productInfoCopy}>
                  <Text parent="h3" i18nKey="products.product3.title"/>
                  <Text parent="h4" i18nKey="products.product3.subtitle"/>
                  <Text parent="p" className={s.productCopy} i18nKey="products.product3Text"/>
                </div>
              </div>
            </div>
            <LinkMore
              className={s.linkMore}
              data-hook="products-get-started products-get-started-wix-code"
              onClick={reportClick}
              data-foldname="products"
            >
              {t('button.getStarted')}
            </LinkMore>
          </div>
        </div>
      </div>
    );
  }
}

export default translate()(Products);
